/* Triumph DATA PLATE | tl-cart.js
   Cart page behaviour for sections/main-cart.liquid ([data-tl-ct] roots). Vanilla ES2017.
   Needs tl-global.js (TL.cart.change, TL.formatMoneyText, TL.announce, TL.onSections); without it,
   or without fetch, the page keeps working as a plain form (updates[] + "Update cart", remove links).

   Behaviour
     - Quantity stepper (-/+) and typed quantities -> TL.cart.change(lineKey, qty) (debounced for the
       stepper, on change/Enter for typing), then a Section Rendering API refresh of this section:
       GET TL.routes.cart?section_id=<section id>, and the [data-tl-ct-part] regions (count, lines,
       totals) are swapped in place, so the note, the checkout button and the accelerated checkout
       buttons are never re-rendered. Changes are queued; the refresh runs once the queue is idle.
     - Remove links -> TL.cart.change(key, 0) + refresh. Focus moves to the next line's name (or to
       the page heading when the cart becomes empty); results are announced politely.
     - Filled -> empty swaps the whole section; empty -> filled (an add from elsewhere) reloads so
       the accelerated checkout buttons initialise.
     - Order note: saved to /cart/update.js as you type (debounced) with a status line; it is also
       posted with the checkout form, so nothing is lost if a save fails.
     - Checkout submits natively (POST /cart with checkout + updates[] + note).
     - 'tl:cart:added' (an add from another part of the page) and a back/forward-cache restore
       refresh the section. Theme editor: re-init on shopify:section:load, tidy on unload. */
(function () {
  'use strict';

  var TL = window.TL = window.TL || {};
  var SEL = '[data-tl-ct]';
  var instances = [];

  function canAjax() {
    return typeof window.fetch === 'function' && typeof window.DOMParser === 'function' &&
      !!TL.cart && typeof TL.cart.change === 'function';
  }
  function route(key, fallback) {
    return (TL.routes && TL.routes[key]) || fallback;
  }
  function money(cents) {
    if (typeof TL.formatMoneyText === 'function') return TL.formatMoneyText(cents);
    return '$' + ((parseInt(cents, 10) || 0) / 100).toFixed(2);
  }
  function announce(msg) {
    if (msg && typeof TL.announce === 'function') TL.announce(msg);
  }
  function itemsText(n) {
    return n + (n === 1 ? ' item' : ' items');
  }
  function toInt(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: false }); } catch (e) { el.focus(); }
  }
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/["\\]/g, '\\$&');
  }
  function findItem(cart, key) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) if (items[i].key === key) return items[i];
    return null;
  }

  function CartPage(root) {
    this.root = root;
    this.id = root.getAttribute('data-section-id') || '';
    this.timers = {};
    this.errors = {};
    this.queue = Promise.resolve();
    this.inflight = 0;
    this.noteTimer = null;
    this.noteSeq = 0;
    this.lastCart = null;
    this.message = '';
    this.stale = false;

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    root.addEventListener('click', this.onClick);
    root.addEventListener('input', this.onInput);
    root.addEventListener('change', this.onChange);
    root.addEventListener('keydown', this.onKeydown);
    root.addEventListener('submit', this.onSubmit);
  }

  CartPage.prototype.destroy = function () {
    var r = this.root;
    r.removeEventListener('click', this.onClick);
    r.removeEventListener('input', this.onInput);
    r.removeEventListener('change', this.onChange);
    r.removeEventListener('keydown', this.onKeydown);
    r.removeEventListener('submit', this.onSubmit);
    for (var k in this.timers) clearTimeout(this.timers[k]);
    this.timers = {};
    clearTimeout(this.noteTimer);
    this.dead = true;
  };

  /* ── lookups ─────────────────────────────────────────────────────────── */
  CartPage.prototype.form = function () {
    return this.root.querySelector('form[data-tl-ct-form]');
  };
  CartPage.prototype.lines = function () {
    return Array.prototype.slice.call(this.root.querySelectorAll('[data-tl-ct-line]'));
  };
  CartPage.prototype.lineFor = function (key) {
    return this.root.querySelector('[data-tl-ct-line][data-key="' + cssEscape(key) + '"]');
  };
  function inputOf(line) { return line && line.querySelector('[data-tl-ct-qty-input]'); }
  function rules(input) {
    var min = Math.max(1, toInt(input.getAttribute('data-min'), 1));
    var step = Math.max(1, toInt(input.getAttribute('data-step'), 1));
    var max = toInt(input.getAttribute('data-max'), Infinity);
    return { min: min, step: step, max: max };
  }
  function snap(v, r) {
    if (v > r.max) v = r.max;
    if (r.step > 1) v = Math.round(v / r.step) * r.step;
    if (v < r.min) v = r.min;
    if (v > r.max) v = r.max;
    return v;
  }

  /* ── stepper ─────────────────────────────────────────────────────────── */
  CartPage.prototype.paint = function (line) {
    var input = inputOf(line);
    if (!input) return;
    var r = rules(input);
    var v = toInt(input.value, r.min);
    var minus = line.querySelector('[data-tl-ct-qty="minus"]');
    var plus = line.querySelector('[data-tl-ct-qty="plus"]');
    if (minus) minus.disabled = v <= r.min;
    if (plus) plus.disabled = v >= r.max;
  };

  CartPage.prototype.step = function (btn) {
    var line = btn.closest('[data-tl-ct-line]');
    var input = inputOf(line);
    if (!input) return;
    var r = rules(input);
    var v = toInt(input.value, toInt(input.getAttribute('data-original'), r.min));
    v = btn.getAttribute('data-tl-ct-qty') === 'plus' ? v + r.step : v - r.step;
    input.value = snap(v, r);
    this.paint(line);
    /* keep keyboard focus usable when the button just became disabled */
    if (btn.disabled) focusEl(input);
    this.schedule(line, 450);
  };

  CartPage.prototype.schedule = function (line, ms) {
    var self = this;
    var key = line.getAttribute('data-key');
    clearTimeout(this.timers[key]);
    this.timers[key] = setTimeout(function () {
      delete self.timers[key];
      self.commit(key);
    }, ms);
  };

  /* ── committing a quantity ───────────────────────────────────────────── */
  CartPage.prototype.commit = function (key) {
    var line = this.lineFor(key);
    var input = inputOf(line);
    if (!input) return;
    if (this.timers[key]) { clearTimeout(this.timers[key]); delete this.timers[key]; }
    var orig = toInt(input.getAttribute('data-original'), 1);
    var raw = String(input.value).trim();
    var qty = toInt(raw, NaN);
    if (raw === '' || isNaN(qty) || qty < 0) {
      input.value = orig;
      this.paint(line);
      return;
    }
    if (qty > 0) qty = snap(qty, rules(input));
    input.value = qty;
    this.paint(line);
    if (qty === orig) {
      /* an earlier refresh was skipped while this edit was pending: sync now */
      if (this.stale && this.inflight === 0 && canAjax()) this.refresh().catch(function () { window.location.reload(); });
      return;
    }
    if (!canAjax()) { this.nativeSubmit(); return; }
    this.change(key, qty, qty === 0 ? line.getAttribute('data-title') : null);
  };

  CartPage.prototype.commitAll = function () {
    var self = this;
    this.lines().forEach(function (line) {
      var input = inputOf(line);
      if (input && String(input.value) !== input.getAttribute('data-original')) self.commit(line.getAttribute('data-key'));
    });
  };

  CartPage.prototype.nativeSubmit = function () {
    var form = this.form();
    if (form) HTMLFormElement.prototype.submit.call(form);
  };

  CartPage.prototype.setBusy = function (key, on) {
    var line = this.lineFor(key);
    if (!line) return;
    line.classList.toggle('is-busy', !!on);
    if (on) line.setAttribute('aria-busy', 'true');
    else line.removeAttribute('aria-busy');
  };

  CartPage.prototype.showError = function (key, msg) {
    var line = this.lineFor(key);
    var box = line && line.querySelector('[data-tl-ct-line-error]');
    if (!box) return false;
    box.textContent = msg || '';
    box.hidden = !msg;
    return true;
  };

  CartPage.prototype.change = function (key, qty, removedTitle) {
    var self = this;
    this.setBusy(key, true);
    this.showError(key, '');
    this.inflight++;
    this.queue = this.queue.then(function () {
      if (self.dead) return null;
      return TL.cart.change(key, qty).then(function (cart) {
        return { cart: cart, error: null };
      }, function (err) {
        return { cart: null, error: err || new Error('') };
      });
    }).then(function (res) {
      self.inflight--;
      if (!res || self.dead) return;
      if (res.cart) self.lastCart = res.cart;
      if (res.error) {
        self.errors[key] = res.error.message || (TL.strings && TL.strings.genericError) || 'The cart could not be updated. Please try again.';
        self.message = self.errors[key];
      } else if (qty === 0) {
        self.message = (removedTitle ? removedTitle + ' removed from your cart. ' : 'Item removed. ') + self.summaryText(res.cart);
      } else {
        var item = findItem(res.cart, key);
        if (item && item.quantity !== qty) self.errors[key] = 'Quantity adjusted to ' + item.quantity + '.';
        /* the server value is the new baseline, even before the refresh lands */
        var input = inputOf(self.lineFor(key));
        if (input && item) input.setAttribute('data-original', String(item.quantity));
        self.message = 'Cart updated. ' + self.summaryText(res.cart);
      }
      return self.refresh();
    }).catch(function () {
      window.location.reload();
    });
    return this.queue;
  };

  CartPage.prototype.summaryText = function (cart) {
    if (!cart) return '';
    var n = toInt(cart.item_count, 0);
    if (n === 0) return 'Your cart is empty.';
    return itemsText(n) + ', subtotal ' + money(cart.items_subtotal_price) + '.';
  };

  /* ── Section Rendering refresh ───────────────────────────────────────── */
  CartPage.prototype.sectionUrl = function () {
    var base = route('cart', '/cart');
    return base + (base.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(this.id);
  };

  CartPage.prototype.refresh = function (force) {
    var self = this;
    /* another change is queued or pending: it refreshes when it lands */
    if (!force && (this.inflight > 0 || Object.keys(this.timers).length > 0)) {
      this.stale = true;
      return Promise.resolve(false);
    }
    return fetch(this.sectionUrl(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      cache: 'no-store'
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.text();
    }).then(function (html) {
      if (self.dead) return false;
      return self.apply(html);
    });
  };

  CartPage.prototype.captureFocus = function () {
    var a = document.activeElement;
    if (!a || a === document.body || !this.root.contains(a)) return null;
    var line = a.closest('[data-tl-ct-line]');
    if (!line) return null;
    return {
      key: line.getAttribute('data-key'),
      role: a.getAttribute('data-tl-ct-focus') || 'title',
      index: this.lines().indexOf(line)
    };
  };

  CartPage.prototype.restoreFocus = function (f) {
    if (!f) return;
    var line = this.lineFor(f.key);
    var el = null;
    if (line) {
      el = line.querySelector('[data-tl-ct-focus="' + f.role + '"]');
      if (!el || el.disabled) el = line.querySelector('[data-tl-ct-focus="input"]');
    } else {
      var lines = this.lines();
      var next = lines.length ? lines[Math.min(Math.max(f.index, 0), lines.length - 1)] : null;
      el = next ? next.querySelector('[data-tl-ct-focus="title"]') : this.root.querySelector('[data-tl-ct-lh]');
    }
    if (el) {
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }
  };

  CartPage.prototype.apply = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector(SEL);
    if (!fresh) { window.location.reload(); return false; }
    /* the shopper edited a quantity while this was loading: their pending change refreshes later */
    if (Object.keys(this.timers).length > 0) { this.stale = true; return false; }
    this.stale = false;
    var was = this.root.getAttribute('data-state');
    var now = fresh.getAttribute('data-state');
    var msg = this.message;
    this.message = '';

    if (was !== now) {
      if (now === 'filled') { window.location.reload(); return false; }
      /* the cart emptied: swap the whole section (empty state) */
      this.root.innerHTML = fresh.innerHTML;
      this.root.setAttribute('data-state', now);
      this.errors = {};
      focusEl(this.root.querySelector('[data-tl-ct-h1]'));
      announce(msg || 'Your cart is empty.');
      return true;
    }

    var focus = this.captureFocus();
    var parts = this.root.querySelectorAll('[data-tl-ct-part]');
    for (var i = 0; i < parts.length; i++) {
      var name = parts[i].getAttribute('data-tl-ct-part');
      var next = fresh.querySelector('[data-tl-ct-part="' + name + '"]');
      if (next && parts[i].parentNode) parts[i].parentNode.replaceChild(document.importNode(next, true), parts[i]);
    }
    this.restoreFocus(focus);

    for (var key in this.errors) this.showError(key, this.errors[key]);
    this.errors = {};
    announce(msg);
    return true;
  };

  /* ── order note ──────────────────────────────────────────────────────── */
  CartPage.prototype.noteStatus = function (text, isError) {
    var st = this.root.querySelector('[data-tl-ct-note-status]');
    if (!st) return;
    st.textContent = text || '';
    st.classList.toggle('is-error', !!isError);
  };

  CartPage.prototype.saveNote = function (area) {
    var self = this;
    clearTimeout(this.noteTimer);
    this.noteTimer = null;
    if (typeof window.fetch !== 'function') return;
    var value = area.value;
    if (value === area.getAttribute('data-saved')) return;
    var seq = ++this.noteSeq;
    this.noteStatus('Saving…');
    fetch(route('cart_update', '/cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ note: value }),
      cache: 'no-store'
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (cart) {
      if (cart && TL.cart) TL.cart.last = cart;
      area.setAttribute('data-saved', value);
      if (seq === self.noteSeq) self.noteStatus('Note saved.');
    }).catch(function () {
      if (seq === self.noteSeq) self.noteStatus('The note could not be saved yet. It is sent with your order when you check out.', true);
    });
  };

  /* ── events ──────────────────────────────────────────────────────────── */
  CartPage.prototype.onClick = function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-tl-ct-qty]');
    if (btn && this.root.contains(btn)) {
      e.preventDefault();
      if (!btn.disabled) this.step(btn);
      return;
    }
    var rm = t.closest('[data-tl-ct-remove]');
    if (rm && this.root.contains(rm) && canAjax()) {
      var line = rm.closest('[data-tl-ct-line]');
      if (!line) return;
      e.preventDefault();
      var key = line.getAttribute('data-key');
      if (this.timers[key]) { clearTimeout(this.timers[key]); delete this.timers[key]; }
      var input = inputOf(line);
      if (input) input.value = 0;
      this.change(key, 0, line.getAttribute('data-title'));
    }
  };

  CartPage.prototype.onInput = function (e) {
    var t = e.target;
    if (t.hasAttribute('data-tl-ct-note')) {
      var self = this;
      clearTimeout(this.noteTimer);
      this.noteStatus('');
      this.noteTimer = setTimeout(function () { self.saveNote(t); }, 800);
      return;
    }
    if (t.hasAttribute('data-tl-ct-qty-input')) {
      var line = t.closest('[data-tl-ct-line]');
      if (line) this.paint(line);
    }
  };

  CartPage.prototype.onChange = function (e) {
    var t = e.target;
    if (t.hasAttribute('data-tl-ct-note')) { this.saveNote(t); return; }
    if (t.hasAttribute('data-tl-ct-qty-input')) {
      var line = t.closest('[data-tl-ct-line]');
      if (line) this.commit(line.getAttribute('data-key'));
    }
  };

  CartPage.prototype.onKeydown = function (e) {
    var t = e.target;
    if ((e.key === 'Enter' || e.keyCode === 13) && t.hasAttribute && t.hasAttribute('data-tl-ct-qty-input')) {
      e.preventDefault();
      var line = t.closest('[data-tl-ct-line]');
      if (line) this.commit(line.getAttribute('data-key'));
    }
  };

  CartPage.prototype.onSubmit = function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[data-tl-ct-form]')) return;
    var sub = e.submitter;
    if (!sub || sub.name === 'checkout') {
      /* native checkout POST carries updates[] + note: drop pending AJAX work */
      for (var k in this.timers) clearTimeout(this.timers[k]);
      this.timers = {};
      clearTimeout(this.noteTimer);
      if (sub) {
        sub.classList.add('is-busy');
        sub.setAttribute('aria-busy', 'true');
      }
      return;
    }
    if (!canAjax()) return;
    e.preventDefault();
    this.commitAll();
  };

  /* ── init ────────────────────────────────────────────────────────────── */
  function init(root) {
    if (root.__tlCart) return;
    var page = new CartPage(root);
    root.__tlCart = page;
    instances.push(page);
    var notes = root.querySelectorAll('[data-tl-ct-note]');
    for (var i = 0; i < notes.length; i++) notes[i].setAttribute('data-saved', notes[i].value);
    page.lines().forEach(function (line) { page.paint(line); });
  }

  function destroy(root) {
    var page = root.__tlCart;
    if (!page) return;
    page.destroy();
    delete root.__tlCart;
    var i = instances.indexOf(page);
    if (i > -1) instances.splice(i, 1);
  }

  function live() {
    return instances.filter(function (p) { return !p.dead && p.root.isConnected; });
  }

  if (typeof TL.onSections === 'function') {
    TL.onSections(SEL, init, destroy);
  } else {
    var run = function (scope) {
      var list = (scope || document).querySelectorAll(SEL);
      for (var i = 0; i < list.length; i++) init(list[i]);
      if (scope && scope.matches && scope.matches(SEL)) init(scope);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { run(document); });
    else run(document);
    document.addEventListener('shopify:section:load', function (e) { run(e.target); });
    document.addEventListener('shopify:section:unload', function (e) {
      var list = e.target.querySelectorAll(SEL);
      for (var i = 0; i < list.length; i++) destroy(list[i]);
    });
  }

  /* an add from elsewhere on the page (e.g. a product tile): bring the cart up to date */
  document.addEventListener('tl:cart:added', function () {
    if (!canAjax()) return;
    live().forEach(function (p) {
      if (p.root.getAttribute('data-state') !== 'filled') { window.location.reload(); return; }
      p.refresh().catch(function () { window.location.reload(); });
    });
  });

  /* back/forward cache: the cart may have changed in another tab */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted || !canAjax()) return;
    live().forEach(function (p) {
      p.refresh(true).catch(function () { window.location.reload(); });
    });
  });
})();
